import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Badge } from './components/ui/badge';

const HelpCenterPage = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedService, setExpandedService] = useState(null);
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToServices = () => {
    // Scroll to the service guides section
    setTimeout(() => {
      const servicesSection = document.getElementById('services-section');
      if (servicesSection) {
        const yOffset = -150; // Offset to account for sticky headers
        const y = servicesSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 100); // Small delay to ensure content is rendered
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const service = params.get('service');
    if (service) {
      setExpandedService(service);
      setTimeout(() => {
        const element = document.getElementById(`service-${service}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location]);

  // Auto-scroll to results when user searches
  useEffect(() => {
    if (searchQuery && searchQuery.length > 0) {
      scrollToServices();
    }
  }, [searchQuery]);

  // Service guides data - organized exactly like the app workflows (5 workflows)
  const serviceGuides = {
    overview: [
      // Overview section - summary of all services
    ],
    writing: [
      // BCTG, CCG, BOCP services
      {
        code: 'BCTG',
        name: 'Book Concept & Title Generator',
        icon: '💡',
        price: '$5 (FREE 3/month)',
        description: 'Generate fresh book concepts and compelling titles',
        overview: 'Overcome writer\'s block by generating multiple book concepts with titles, taglines, and story elements based on your genre, themes, and audience.',
        stepByStep: ['Navigate to Dashboard → Click BCTG card', 'Select genre (Fiction, Non-Fiction, etc.)', 'Enter 2-3 themes or topics', 'Specify target audience', 'Choose tone (Serious, Light, Dark, etc.)', 'Click Generate → Wait 30-60 seconds', 'Review 3 complete concepts', 'Save favorites or regenerate'],
        examples: [{ title: 'Thriller', input: 'Genre: Thriller | Themes: Technology, Privacy | Audience: Adults 25-45', output: 'Title: "The Algorithm Knows" | Premise: Data scientist discovers company using AI to manipulate decisions...' }],
        bestPractices: ['Be specific about target audience', 'Include 2-3 themes for richer concepts', 'Try different tone combinations', 'Generate multiple batches', 'Save concepts immediately', 'Use as starting points, add your spin'],
        relatedServices: [{ code: 'CCG', name: 'Character Creator', reason: 'Develop characters for your concept' }, { code: 'BOCP', name: 'Book Outline', reason: 'Turn concept into full outline' }],
        isPremium: false, isFree: true
      },
      {
        code: 'CCG',
        name: 'Character Creator Generator',
        icon: '🎭',
        price: '$7',
        description: 'Create rich, multi-dimensional characters',
        overview: 'Build fully realized characters with backstories, motivations, personalities, and character arcs.',
        stepByStep: ['Access Character Creator', 'Select character role', 'Enter genre and setting', 'Describe character function', 'Add specific traits', 'Choose age range', 'Click Create → Wait 45-60 seconds', 'Review complete profile', 'Save to library'],
        examples: [{ title: 'Fantasy Protagonist', input: 'Role: Protagonist | Genre: Fantasy | Function: Young mage | Traits: Curious, reckless, loyal', output: 'Complete profile with personality, backstory, motivations, relationships, arc...' }],
        bestPractices: ['Start with character\'s role', 'Be clear about genre', 'Provide primary conflict', 'Mention key relationships', 'Leave room for creative surprises', 'Create multiple characters', 'Add your unique touches'],
        relatedServices: [{ code: 'BCTG', name: 'Book Concept', reason: 'Start with concept first' }, { code: 'BOCP', name: 'Book Outline', reason: 'Place characters in story structure' }],
        isPremium: false
      },
      {
        code: 'BOCP',
        name: 'Book Outline & Chapter Planner',
        icon: '📚',
        price: '$15 (50% OFF subscribers)',
        description: 'Structure your entire book with chapter-by-chapter outlines',
        overview: 'Create comprehensive roadmap with chapter breakdowns, scene descriptions, pacing notes, and narrative arc guidance.',
        stepByStep: ['Open Book Outline Planner', 'Enter book concept', 'Specify target word/chapter count', 'Select genre and audience', 'List major plot points', 'Indicate structure preference', 'Add key characters', 'Click Generate → Wait 2-3 minutes', 'Review chapter breakdown', 'Download or save'],
        examples: [{ title: 'Mystery Novel', input: 'Premise: Detective investigates art thefts | 80k words | Plot: First theft, connection revealed, red herring, culprit', output: 'Chapter-by-chapter with scene summaries, pacing notes, word counts...' }],
        bestPractices: ['Have clear premise first', 'Be realistic about word count', 'Include major plots, not over-specify', 'Specify pacing preferences', 'Use as flexible guide', 'Adjust chapter lengths by genre', 'Create separate outlines for POVs'],
        relatedServices: [{ code: 'BCTG', name: 'Book Concept', reason: 'Generate premise first' }, { code: 'PDE', name: 'Developmental Editor', reason: 'Get feedback after drafting' }],
        isPremium: true
      }
    ],
    editing: [
      {
        code: 'ABG',
        name: 'Author Biography Generator',
        icon: '👤',
        price: '$5',
        description: 'Create compelling author bios for various platforms',
        overview: 'Generate professional bios in multiple lengths - short (50-100 words), medium (150-200), long (400-500).',
        stepByStep: ['Navigate to Author Biography', 'Enter writing background', 'List published works/credentials', 'Include education/expertise', 'Add personal elements (optional)', 'Select tone', 'Specify key points to emphasize', 'Click Generate', 'Receive 3 versions', 'Edit and personalize'],
        examples: [{ title: 'Mystery Author', input: 'Former prosecutor, 3 novels | Law degree | Portland, OR | Professional tone', output: '3 versions: Short, Medium, Long with legal background emphasis...' }],
        bestPractices: ['Be honest about credentials', 'Include personal touch', 'Update regularly', 'Match tone to genre', 'Lead with strongest credential', 'Mention current project', 'Keep short bios under 100 words'],
        relatedServices: [{ code: 'BVADM', name: 'Book Viability', reason: 'Position effectively in market' }, { code: 'MMKG', name: 'Media Kit', reason: 'Complete press kit with bio' }],
        isPremium: false
      },
      {
        code: 'BVADM',
        name: 'Book Viability & Development Analysis',
        icon: '📊',
        price: '$12 (50% OFF subscribers)',
        description: 'Comprehensive market analysis and viability assessment',
        overview: 'Flagship service: Deep analysis of commercial potential, competitive positioning, comparable titles, development suggestions, market positioning, monetization strategy, marketing roadmap, AND digital marketing assets.',
        stepByStep: ['Access BVADM', 'Upload manuscript (min 10k words)', 'Provide book details', 'Describe target audience', 'Share author info', 'Indicate publishing goals', 'Add specific questions', 'Click Analyze → Wait 8-12 minutes', 'Review 9-section report', 'Download as PDF'],
        examples: [{ title: 'Literary Fiction', input: '75k words, immigrant family NYC | Adult 30-55 | Debut author, MFA | Traditional publishing', output: '9 sections: Scores, Comparable titles, Development recs, Marketing roadmap, SEO keywords...' }],
        bestPractices: ['Submit polished draft', 'Be thorough with audience description', 'Review comparable titles carefully', 'Use monetization/marketing as roadmap', 'Share with agents/publishers', 'Re-analyze after revisions', 'Implement digital marketing assets'],
        relatedServices: [{ code: 'PDE', name: 'Developmental Editor', reason: 'Fix craft before market analysis' }, { code: 'MMG', name: 'Metadata Generator', reason: 'Use BVADM insights' }],
        isPremium: true
      },
      {
        code: 'PDE',
        name: 'Professional Developmental Editor',
        icon: '✍️',
        price: '$20 (50% OFF subscribers)',
        description: 'Comprehensive manuscript analysis and editing feedback',
        overview: 'In-depth analysis of structure, pacing, character development, plot coherence, and narrative effectiveness with specific improvement recommendations.',
        stepByStep: ['Navigate to PDE', 'Upload manuscript (up to 150k words)', 'Provide genre and audience', 'List specific concerns', 'Indicate manuscript stage', 'Add context (series, standalone)', 'Click Analyze → Wait 5-8 minutes', 'Review editorial report', 'Download as PDF', 'Re-submit for comparison (optional)'],
        examples: [{ title: 'Fantasy Novel', input: '95k fantasy, first in series | Concerns: Pacing, character motivation | Second draft', output: 'Structure analysis, character assessment, pacing evaluation, specific recommendations...' }],
        bestPractices: ['Submit complete chapters', 'Be specific about concerns', 'Provide audience context', 'Use on 2nd/3rd drafts', 'Read full report before changes', 'Fix structural issues first', 'Re-analyze after major revisions'],
        relatedServices: [{ code: 'BOCP', name: 'Book Outline', reason: 'Re-outline problem sections' }, { code: 'BVADM', name: 'Book Viability', reason: 'Assess market potential' }],
        isPremium: true
      }
    ],
    production: [
      {
        code: 'ERMT',
        name: 'EPUB Reader & Manuscript Transformer',
        icon: '📕',
        price: '$20/$15 (50% OFF subscribers)',
        description: 'Convert manuscripts to professional EPUB format',
        overview: 'Transform manuscript into professional EPUB with formatting, metadata, table of contents, proper e-reader display.',
        stepByStep: ['Access EPUB Transformer', 'Upload manuscript (DOCX recommended)', 'Provide metadata (title, author, description, ISBN)', 'Upload cover image (1600x2400px)', 'Specify genre and details', 'Choose formatting options', 'Enable auto TOC (recommended)', 'Click Transform → Wait 3-5 minutes', 'Download EPUB', 'Test in multiple readers'],
        examples: [{ title: 'Fiction Novel', input: '80k thriller DOCX | High-res cover | Complete metadata | Auto-TOC', output: 'Professional EPUB with metadata, clickable TOC, formatted chapters, ready for distribution...' }],
        bestPractices: ['Use consistent formatting in source', 'Provide high-quality cover', 'Fill out all metadata', 'Test on multiple devices', 'Keep formatting simple', 'Include front matter', 'Generate separate EPUB for editions'],
        relatedServices: [{ code: 'IBG', name: 'ISBN Barcode', reason: 'Barcode for print version' }, { code: 'AKSW', name: 'KDP Wizard', reason: 'Publishing guide' }],
        isPremium: true
      },
      {
        code: 'PMPB',
        name: 'Professional Media Press Kit Builder',
        icon: '📄',
        price: '$3',
        description: 'Create comprehensive press kits and promotional materials',
        overview: 'Generate complete press materials: press releases, media pitches, interview questions, fact sheets, promotional copy.',
        stepByStep: ['Navigate to Press Kit Builder', 'Enter book information', 'Provide author bio', 'List target media outlets', 'Identify key selling points', 'Specify unique aspects', 'Add endorsements/reviews', 'Click Build → Wait 90 seconds', 'Receive 5-component kit', 'Customize and begin outreach'],
        examples: [{ title: 'Debut Novel', input: 'Historical fiction, WWII codebreakers | Former cryptographer | Timely cybersecurity relevance | Target: Book blogs, history podcasts', output: 'Press release, media pitch, interview Q&A, fact sheet, promotional copy...' }],
        bestPractices: ['Lead with strongest hook', 'Tailor for different media', 'Include author expertise', 'Provide ready-to-use quotes', 'Update with new reviews', 'Send 2-3 months before pub', 'Follow up personally'],
        relatedServices: [{ code: 'ABG', name: 'Author Bio', reason: 'Professional bio for kit' }, { code: 'MMKG', name: 'Media Kit', reason: 'Comprehensive marketing' }],
        isPremium: false
      },
      {
        code: 'IBG',
        name: 'ISBN Barcode Generator',
        icon: '📱',
        price: 'FREE (Unlimited)',
        description: 'Generate professional ISBN barcodes for book covers',
        overview: 'Create print-ready ISBN-13 barcodes. Essential for print books. Generate unlimited barcodes for all editions.',
        stepByStep: ['Click ISBN Barcode Generator', 'Enter 13-digit ISBN', 'Add retail price (optional)', 'Select currency', 'Choose format (PNG/SVG)', 'Click Generate', 'Download high-res image (300 DPI)', 'Place on back cover (lower right)'],
        examples: [{ title: 'Paperback', input: 'ISBN: 978-0-123456-78-9 | Price: $14.99 USD', output: 'High-res barcode with ISBN and price extension, print-ready' }],
        bestPractices: ['Double-check ISBN before generating', 'Generate separate for each edition', 'Use PNG for most designs', 'Verify with printer template', 'Test scan with smartphone', 'Organize by edition', 'Price optional for Amazon KDP'],
        relatedServices: [{ code: 'QRG', name: 'QR Code', reason: 'Add QR codes too' }, { code: 'AKSW', name: 'KDP Wizard', reason: 'Publishing guide' }],
        isPremium: false, isFree: true
      },
      {
        code: 'QRG',
        name: 'QR Code Generator',
        icon: '📱',
        price: 'FREE (Unlimited)',
        description: 'Generate custom QR codes for book marketing',
        overview: 'Create professional QR codes linking to book page, website, social media, newsletter. Unlimited codes at no cost.',
        stepByStep: ['Click QR Code Generator', 'Enter target URL', 'Customize (color, size, error correction)', 'Preview QR code', 'Click Generate', 'Download high-res (PNG/SVG)', 'Test with smartphone', 'Add to marketing materials'],
        examples: [{ title: 'Book Launch', input: 'Amazon book page | Custom colors (teal/gold) | Large size for poster', output: 'Professional QR code in brand colors, poster-ready' }],
        bestPractices: ['Use URL shorteners for cleaner codes', 'Test on multiple phones', 'Include clear call-to-action text', 'Minimum 2cm x 2cm size', 'Place where people have time to scan', 'Track with unique URLs', 'Use redirect services', 'Print with good contrast'],
        relatedServices: [{ code: 'IBG', name: 'ISBN Barcode', reason: 'Add both to print books' }, { code: 'MMKG', name: 'Media Kit', reason: 'Integrate in marketing' }],
        isPremium: false, isFree: true
      }
    ],
    marketing: [
      {
        code: 'CCAT',
        name: 'Competitive & Comparison Analysis',
        icon: '🔍',
        price: '$7',
        description: 'Analyze competitive landscape and positioning',
        overview: 'Research competing titles, provide strategic insights for positioning and differentiation. Understand market, identify gaps.',
        stepByStep: ['Open Competitive Analysis', 'Enter genre and sub-genre', 'Provide target audience', 'List key themes', 'Add comparable titles (optional)', 'Indicate price point', 'Click Analyze → Wait 2-3 minutes', 'Review report: competitive titles, positioning matrix, differentiation strategies', 'Use insights for marketing copy'],
        examples: [{ title: 'Urban Fantasy', input: 'Genre: Urban Fantasy | Sub: Supernatural detective | Audience: 25-40 | Unique: Female lead, New Orleans, voodoo', output: 'Competitive titles, market positioning, differentiation strategies, pricing recommendations...' }],
        bestPractices: ['Be specific about sub-genre', 'Identify unique elements first', 'Study recommended competitors', 'Use for book description', 'Find gaps, don\'t copy', 'Consider direct and indirect competitors', 'Update if major changes'],
        relatedServices: [{ code: 'BVADM', name: 'Book Viability', reason: 'Deeper market analysis' }, { code: 'MMG', name: 'Metadata', reason: 'Use for keyword optimization' }],
        isPremium: false
      },
      {
        code: 'BREC',
        name: 'Book Review & Editorial Content Creator',
        icon: '📣',
        price: '$7',
        description: 'Generate professional book reviews and editorial content',
        overview: 'Create authentic reviews and editorial content for various platforms. Useful for understanding review perspectives.',
        stepByStep: ['Navigate to Review Creator', 'Enter book information', 'Select platform (Amazon, Goodreads, blog, etc.)', 'Choose tone and perspective', 'Specify length (short/medium/long)', 'List key points to emphasize', 'Click Generate', 'Receive professional content', 'Customize for needs'],
        examples: [{ title: 'Amazon Review', input: 'Contemporary romance "The Last Summer" | Amazon | Enthusiastic | Medium | Emphasize: Emotional depth, chemistry, setting', output: '5-star reader review with emotional response, character praise, setting love...' }],
        bestPractices: ['Match tone to platform', 'Use for marketing prep', 'Create book club guides', 'Generate multiple perspectives', 'Adapt rather than use verbatim', 'Test positioning angles', 'Generate for blog/newsletter'],
        relatedServices: [{ code: 'PMPB', name: 'Press Kit', reason: 'Sample reviews in kit' }, { code: 'SMMK', name: 'Social Media Kit', reason: 'Adapt quotes for social' }],
        isPremium: false
      },
      {
        code: 'MMG',
        name: 'Metadata & Marketing Generator',
        icon: '🏷️',
        price: '$10',
        description: 'Create optimized book metadata for discoverability',
        overview: 'Generate SEO-optimized metadata: book descriptions (4 versions), 7 keyword phrases, BISAC codes, 50+ search terms.',
        stepByStep: ['Access Metadata Generator', 'Enter synopsis and themes', 'Specify genre precisely', 'Describe target audience', 'List comparable books', 'Indicate special features', 'Click Generate → Wait 90 seconds', 'Receive complete package', 'Implement when setting up book'],
        examples: [{ title: 'Sci-Fi', input: 'Hard sci-fi, AI consciousness | Ethics, humanity\'s future | Comps: "Ancillary Justice" | Audience: Adult sci-fi readers', output: '4 descriptions, 7 keywords, BISAC codes, 50+ search terms...' }],
        bestPractices: ['Research successful books first', 'Use all 7 keyword slots', 'Choose BISAC carefully', 'Test different descriptions', 'Include reader benefits', 'Update keywords seasonally', 'Monitor keyword performance'],
        relatedServices: [{ code: 'CCAT', name: 'Competitive Analysis', reason: 'Research metadata strategies' }, { code: 'AKSW', name: 'KDP Wizard', reason: 'Metadata entry guide' }],
        isPremium: false
      },
      {
        code: 'KSG',
        name: 'Keynote Speech Generator',
        icon: '🎤',
        price: '$15 (50% OFF subscribers)',
        description: 'Create engaging keynote speeches for author events',
        overview: 'Generate compelling speeches for launches, conferences, panels, workshops. Tailored to themes, style, and audience.',
        stepByStep: ['Open Keynote Speech Generator', 'Specify event type', 'Enter target speaking time', 'Describe audience', 'List key messages/themes', 'Include personal anecdotes', 'Choose tone', 'Add requirements (Q&A, reading)', 'Click Generate → Wait 2-3 minutes', 'Review complete speech', 'Practice and personalize'],
        examples: [{ title: 'Book Launch', input: 'Bookstore launch | 15 min | 50 people | Themes: Resilience, family, community | Hometown setting story | Warm, inspirational', output: 'Complete speech: Opening, body sections, transitions, conclusion, call-to-action...' }],
        bestPractices: ['Include personal stories', 'Practice aloud', 'Time yourself', 'Build in pauses', 'Have backup shorter version', 'Connect to universal experiences', 'End with clear call-to-action'],
        relatedServices: [{ code: 'ABG', name: 'Author Bio', reason: 'Bio for introduction' }, { code: 'MMKG', name: 'Media Kit', reason: 'Complete event materials' }],
        isPremium: true
      },
      {
        code: 'MMKG',
        name: 'Marketing Media Kit Generator',
        icon: '📰',
        price: '$20 (50% OFF subscribers)',
        description: 'Create comprehensive media kits for book marketing',
        overview: 'Complete marketing package: press materials, social media assets, marketing copy, promotional content, ad templates.',
        stepByStep: ['Navigate to Media Kit Generator', 'Provide book info and selling points', 'Describe target audience', 'Include author bio and platform', 'List marketing channels', 'Specify unique angles', 'Indicate pub date/timeline', 'Click Generate → Wait 3-4 minutes', 'Receive complete kit', 'Customize and deploy'],
        examples: [{ title: 'Thriller Launch', input: 'Psychological thriller, memory manipulation | "Gone Girl" fans | Former therapist, 12K Instagram | Multi-channel | Real research hook', output: 'Press release, 15 social posts, email sequences, ad copy, media pitches...' }],
        bestPractices: ['Generate 2-3 months before pub', 'Customize for your voice', 'Test ad copy versions', 'Schedule social in advance', 'Update with reviews', 'Create separate for audience segments', 'Track performance'],
        relatedServices: [{ code: 'SMMK', name: 'Social Media Kit', reason: 'Deeper social strategy' }, { code: 'PMPB', name: 'Press Kit', reason: 'Focused press materials' }],
        isPremium: true
      },
      {
        code: 'SMMK',
        name: 'Social Media Marketing Kit',
        icon: '📱',
        price: '$25 (50% OFF subscribers)',
        description: 'Generate complete social media campaign with content calendar',
        overview: 'Comprehensive social strategy: 20-30 post templates, platform-specific content, hashtags, content calendar, engagement tactics for Instagram, Facebook, Twitter/X, TikTok.',
        stepByStep: ['Access Social Media Kit', 'Enter book info and themes', 'Describe audience social habits', 'Select platforms', 'List visual/shareable elements', 'Specify content preferences', 'Indicate posting frequency', 'Add brand guidelines', 'Click Generate → Wait 2-3 minutes', 'Receive complete kit', 'Schedule and post'],
        examples: [{ title: 'Romance', input: 'Contemporary romance, wine country | Romance readers 25-45 | Instagram/TikTok | Vineyard visuals, couple chemistry | Daily Instagram', output: '20 Instagram posts, platform hashtags, TikTok ideas, content calendar, engagement tactics...' }],
        bestPractices: ['Start months before pub', 'Mix promotional and value content (80/20)', 'Use platform-specific strategies', 'Engage authentically', 'Save high-performing templates', 'Monitor analytics', 'Collaborate with other authors', 'Schedule in batches'],
        relatedServices: [{ code: 'MMKG', name: 'Media Kit', reason: 'Broader marketing strategy' }, { code: 'BVADM', name: 'Book Viability', reason: 'Roadmap informs social' }],
        isPremium: true
      },
      {
        code: 'AKSW',
        name: 'Amazon KDP Submission Wizard',
        icon: '📦',
        price: '$15 (50% OFF subscribers)',
        description: 'Step-by-step guide for Amazon KDP book submission',
        overview: 'Customized guide for KDP: step-by-step instructions, field guidance, optimization tips, category recommendations, pricing strategy, KDP Select guidance.',
        stepByStep: ['Open KDP Wizard', 'Select format (eBook/paperback/both)', 'Indicate pricing strategy', 'Choose target markets', 'Provide book details', 'List goals', 'Specify KDP Select interest', 'Click Generate → Wait 2 minutes', 'Receive complete guide', 'Follow step-by-step'],
        examples: [{ title: 'Debut Fiction', input: 'eBook + paperback | Competitive pricing | Worldwide | Mystery/Thriller | Goal: Reviews, readership | Considering KDP Select', output: 'Complete guide: Account setup, manuscript prep, field entries, categories, pricing, KDP Select recommendation...' }],
        bestPractices: ['Have materials ready first', 'Choose categories carefully', 'Price strategically', 'Consider KDP Select for first 90 days', 'Upload high-quality cover', 'Proofread description', 'Preview on multiple devices', 'Have launch plan ready'],
        relatedServices: [{ code: 'MMG', name: 'Metadata', reason: 'Optimize for KDP' }, { code: 'ERMT', name: 'EPUB Transformer', reason: 'Create ebook file' }],
        isPremium: true
      }
    ]
  };

  const platformGuides = [
    { title: 'Getting Started', icon: '🚀', content: 'Welcome to Digital Ink AI—your AI-powered creative partner. Explore 15+ services in four categories. Start with services matching your needs: brainstorming (BCTG), characters (CCG), outlining (BOCP), or analysis (BVADM, PDE).' },
    { title: 'Understanding Digital Ink AI', icon: '💡', content: 'We\'re NOT a ghostwriting service. We empower human authors with AI-powered insights, strategic guidance, and professional tools. Your creativity remains at center; we amplify it.' },
    { title: 'Navigating Dashboard', icon: '🎯', content: 'Services organized in four workflows: Writing & Development, Editing & Enhancement, Design & Production, Marketing & Promotion. Each card shows usage stats, pricing, and quick access.' },
    { title: 'Pricing Models', icon: '💳', content: 'SUBSCRIBERS ($29/month, $288/year): Unlimited standard services + 50% off premium. À LA CARTE: Pay per use ($3-$25). STUDENTS: Free standard services. ALL USERS: 3 free BCTG/month. Free tools (IBG, QRG) unlimited.' },
    { title: 'Workflow Recommendations', icon: '📋', content: 'Pre-writing: BCTG→CCG→BOCP. Drafting: Use outline. Revision: PDE→BVADM. Pre-pub: MMG→PMPB→MMKG→SMMK→AKSW. Production: ERMT→IBG→QRG. Marketing: CCAT→BREC→MMKG→KSG.' },
    { title: 'Support', icon: '🆘', content: 'Service help: Click "Need Help?" icon. Platform questions: FAQ or Help Center. Technical issues: Contact us directly. We resolve problems quickly!' }
  ];

  const filterServices = (services) => {
    if (!searchQuery) return services;
    const query = searchQuery.toLowerCase();
    return services.filter(service => 
      service.name.toLowerCase().includes(query) ||
      service.description.toLowerCase().includes(query) ||
      service.code.toLowerCase().includes(query) ||
      service.overview.toLowerCase().includes(query) ||
      service.stepByStep.some(step => step.toLowerCase().includes(query)) ||
      service.bestPractices.some(practice => practice.toLowerCase().includes(query))
    );
  };

  const getFilteredServices = () => {
    if (activeCategory === 'all') {
      return {
        writing: filterServices(serviceGuides.writing || []),
        editing: filterServices(serviceGuides.editing || []),
        production: filterServices(serviceGuides.production || []),
        marketing: filterServices(serviceGuides.marketing || [])
      };
    }
    if (activeCategory === 'overview') {
      // Overview shows all services
      return {
        writing: filterServices(serviceGuides.writing || []),
        editing: filterServices(serviceGuides.editing || []),
        production: filterServices(serviceGuides.production || []),
        marketing: filterServices(serviceGuides.marketing || [])
      };
    }
    return { [activeCategory]: filterServices(serviceGuides[activeCategory] || []) };
  };

  const filteredServices = getFilteredServices();
  const totalResults = Object.values(filteredServices).reduce((sum, category) => sum + (category?.length || 0), 0);

  const categoryInfo = {
    overview: { name: 'Overview', icon: '📋', color: 'slate' },
    writing: { name: 'Writing & Ideation', icon: '✍️', color: 'blue' },
    editing: { name: 'Editing & Enhancement', icon: '📝', color: 'purple' },
    production: { name: 'Design & Production', icon: '🎨', color: 'rose' },
    marketing: { name: 'Marketing & Promotion', icon: '📢', color: 'orange' }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white relative">
      {/* Top Navigation Bar */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-5">
          <div className="flex items-center justify-between">
            {/* Logo - 150% Larger (64px → 160px) */}
            <div className="flex items-center gap-6">
              <img 
                src="/Digital_Ink_AI_Logo.png" 
                alt="Digital Ink AI" 
                className="h-40 w-40 object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div>
                <h2 className="text-4xl font-bold text-slate-800">Digital Ink AI</h2>
                <p className="text-lg text-slate-600">Help Center</p>
              </div>
            </div>
            
            {/* Navigation Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="border-slate-300"
              >
                🏠 Home
              </Button>
              <Button
                onClick={() => navigate(user ? '/user_dashboard' : '/login')}
                className="bg-blue-600 hover:bg-blue-700"
              >
                📊 Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold mb-3">📚 Comprehensive Service Guides</h1>
            <p className="text-lg text-blue-100 max-w-3xl mx-auto">
              Complete documentation for all 15 AI-powered authorship services
            </p>
          </div>

          {/* Search Bar - Fixed Visibility */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <Input
                type="text"
                placeholder="🔍 Search services, guides, workflows, or best practices..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full py-5 px-6 text-base rounded-lg border-2 border-white bg-white text-slate-800 placeholder:text-slate-500 shadow-lg focus:ring-2 focus:ring-white"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-700 text-xl font-bold"
                >
                  ✕
                </button>
              )}
            </div>
            {searchQuery && (
              <p className="text-center mt-3 text-blue-100 font-semibold">
                Found {totalResults} result{totalResults !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Category Filter - Sticky - All 5 Workflows */}
      {!searchQuery && (
        <div className="sticky top-[180px] z-40 bg-white border-b border-slate-200 shadow-sm">
          <div className="max-w-6xl mx-auto px-4 py-3">
            <div className="flex flex-wrap gap-2 justify-center">
              <Button
                onClick={() => { setActiveCategory('all'); setExpandedService(null); scrollToServices(); }}
                variant={activeCategory === 'all' ? 'default' : 'outline'}
                size="sm"
                className={activeCategory === 'all' ? 'bg-slate-700 hover:bg-slate-800' : 'border-slate-400 text-slate-700 hover:bg-slate-50'}
              >
                All Services
              </Button>
              <Button
                onClick={() => { setActiveCategory('overview'); setExpandedService(null); scrollToServices(); }}
                variant={activeCategory === 'overview' ? 'default' : 'outline'}
                size="sm"
                className={activeCategory === 'overview' ? 'bg-slate-600 hover:bg-slate-700' : 'border-slate-500 text-slate-600 hover:bg-slate-50'}
              >
                📋 Overview
              </Button>
              <Button
                onClick={() => { setActiveCategory('writing'); setExpandedService(null); scrollToServices(); }}
                variant={activeCategory === 'writing' ? 'default' : 'outline'}
                size="sm"
                className={activeCategory === 'writing' ? 'bg-blue-600 hover:bg-blue-700' : 'border-blue-600 text-blue-600 hover:bg-blue-50'}
              >
                ✍️ Writing & Ideation
              </Button>
              <Button
                onClick={() => { setActiveCategory('editing'); setExpandedService(null); scrollToServices(); }}
                variant={activeCategory === 'editing' ? 'default' : 'outline'}
                size="sm"
                className={activeCategory === 'editing' ? 'bg-purple-600 hover:bg-purple-700' : 'border-purple-600 text-purple-600 hover:bg-purple-50'}
              >
                📝 Editing & Enhancement
              </Button>
              <Button
                onClick={() => { setActiveCategory('production'); setExpandedService(null); scrollToServices(); }}
                variant={activeCategory === 'production' ? 'default' : 'outline'}
                size="sm"
                className={activeCategory === 'production' ? 'bg-rose-600 hover:bg-rose-700' : 'border-rose-600 text-rose-600 hover:bg-rose-50'}
              >
                🎨 Design & Production
              </Button>
              <Button
                onClick={() => { setActiveCategory('marketing'); setExpandedService(null); scrollToServices(); }}
                variant={activeCategory === 'marketing' ? 'default' : 'outline'}
                size="sm"
                className={activeCategory === 'marketing' ? 'bg-orange-600 hover:bg-orange-700' : 'border-orange-600 text-orange-600 hover:bg-orange-50'}
              >
                📢 Marketing & Promotion
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        
        {/* Brand Section */}
        <div className="mb-10">
          <Card className="bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <span className="text-2xl">✨</span>
                What Digital Ink AI Is (and Isn't)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold text-green-700 mb-2">✅ What We Are</h3>
                  <ul className="space-y-1 text-sm text-slate-700">
                    <li>• Your AI-powered creative partner</li>
                    <li>• Strategic insights provider</li>
                    <li>• Tools to enhance YOUR creativity</li>
                    <li>• Professional authorship services</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-red-700 mb-2">❌ What We're Not</h3>
                  <ul className="space-y-1 text-sm text-slate-700">
                    <li>• NOT a ghostwriting service</li>
                    <li>• NOT replacing your voice</li>
                    <li>• NOT doing all the work for you</li>
                    <li>• NOT a content mill</li>
                  </ul>
                </div>
              </div>
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-slate-700 text-center">
                  <strong>Our Philosophy:</strong> Human authors with AI-powered insights—enhancing your creativity, not replacing it.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Platform Guides */}
        <div className="mb-10">
          <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-2">
            <span>🎯</span> Platform Guides
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {platformGuides.map((guide, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <span className="text-xl">{guide.icon}</span>
                    {guide.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-slate-600 leading-relaxed">{guide.content}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Search Results Indicator */}
        {searchQuery && (
          <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
            <p className="text-center text-slate-700 font-semibold">
              🔍 Searching for: "<span className="text-blue-600">{searchQuery}</span>" 
              <span className="ml-2">- Found {totalResults} result{totalResults !== 1 ? 's' : ''}</span>
            </p>
            {totalResults === 0 && (
              <p className="text-center text-slate-600 text-sm mt-2">
                Try different keywords or <button onClick={() => setSearchQuery('')} className="text-blue-600 underline hover:text-blue-700">clear search</button>
              </p>
            )}
          </div>
        )}

        {/* Service Guides */}
        <div id="services-section" className="space-y-12">
          {Object.entries(filteredServices).map(([category, services]) => {
            if (!services || services.length === 0) return null;
            const info = categoryInfo[category];
            
            return (
              <div key={category}>
                <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-2 border-b-2 border-slate-200 pb-3">
                  <span className="text-3xl">{info.icon}</span>
                  {info.name}
                  <Badge className="ml-auto">{services.length} Services</Badge>
                </h2>
                
                <div className="space-y-6">
                  {services.map((service) => (
                    <Card key={service.code} id={`service-${service.code}`} className={`hover:shadow-xl transition-all ${expandedService === service.code ? 'ring-2 ring-blue-500' : ''} ${searchQuery ? 'bg-yellow-50 border-2 border-yellow-300' : ''}`}>
                      <CardHeader className="cursor-pointer" onClick={() => setExpandedService(expandedService === service.code ? null : service.code)}>
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3 flex-1">
                            <span className="text-3xl">{service.icon}</span>
                            <div className="flex-1">
                              <CardTitle className="text-lg mb-2">{service.name}</CardTitle>
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant="outline" className="text-xs">{service.code}</Badge>
                                <Badge className={`text-xs ${service.isPremium ? 'bg-yellow-400 text-yellow-900' : service.isFree ? 'bg-green-500' : 'bg-blue-500'}`}>
                                  {service.isPremium ? 'PREMIUM' : service.isFree ? 'FREE' : 'STANDARD'}
                                </Badge>
                                <span className="text-xs text-slate-600 font-semibold">{service.price}</span>
                              </div>
                              <p className="text-sm text-slate-600">{service.description}</p>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" className="text-blue-600">
                            {expandedService === service.code ? '▲ Close' : '▼ Expand'}
                          </Button>
                        </div>
                      </CardHeader>

                      {expandedService === service.code && (
                        <CardContent className="space-y-4 border-t pt-4">
                          <div>
                            <h3 className="font-bold text-slate-800 mb-2 flex items-center gap-2 text-sm">📖 Overview</h3>
                            <p className="text-sm text-slate-700">{service.overview}</p>
                          </div>

                          <div>
                            <h3 className="font-bold text-slate-800 mb-2 flex items-center gap-2 text-sm">🎯 Step-by-Step</h3>
                            <div className="space-y-2">
                              {service.stepByStep.map((step, idx) => (
                                <div key={idx} className="flex gap-2 p-2 bg-blue-50 rounded text-xs">
                                  <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">{idx + 1}</div>
                                  <p className="text-slate-700 flex-1">{step}</p>
                                </div>
                              ))}
                            </div>
                          </div>

                          {service.examples && service.examples.length > 0 && (
                            <div>
                              <h3 className="font-bold text-slate-800 mb-2 flex items-center gap-2 text-sm">💡 Examples</h3>
                              {service.examples.map((ex, idx) => (
                                <div key={idx} className="border rounded p-3 bg-slate-50 text-xs">
                                  <h4 className="font-semibold mb-1">{ex.title}</h4>
                                  <p><span className="font-semibold text-green-700">Input:</span> {ex.input}</p>
                                  <p><span className="font-semibold text-blue-700">Output:</span> {ex.output}</p>
                                </div>
                              ))}
                            </div>
                          )}

                          <div>
                            <h3 className="font-bold text-slate-800 mb-2 flex items-center gap-2 text-sm">⭐ Best Practices</h3>
                            <ul className="space-y-1 text-xs text-slate-700">
                              {service.bestPractices.map((p, idx) => (
                                <li key={idx} className="flex gap-2"><span className="text-amber-600">•</span><span>{p}</span></li>
                              ))}
                            </ul>
                          </div>

                          {service.relatedServices && (
                            <div>
                              <h3 className="font-bold text-slate-800 mb-2 flex items-center gap-2 text-sm">🔗 Related Services</h3>
                              <div className="grid md:grid-cols-2 gap-2">
                                {service.relatedServices.map((rel, idx) => (
                                  <button key={idx} onClick={() => { setExpandedService(rel.code); document.getElementById(`service-${rel.code}`)?.scrollIntoView({ behavior: 'smooth' }); }} className="border rounded p-2 hover:bg-slate-50 text-left">
                                    <div className="text-xs font-semibold text-blue-600">{rel.code} - {rel.name} →</div>
                                    <p className="text-xs text-slate-600">{rel.reason}</p>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="flex gap-3 pt-3 border-t">
                            <Button onClick={() => navigate('/user_dashboard')} className="flex-1 bg-blue-600" size="sm">Try {service.code} →</Button>
                            <Button onClick={() => setExpandedService(null)} variant="outline" size="sm">Close</Button>
                          </div>

                          <div className="bg-amber-50 border border-amber-200 rounded p-3">
                            <p className="text-xs text-amber-900">
                              <strong>Need Help?</strong> Technical issues? <a href="/contact" className="text-blue-600 underline">Contact support</a>
                            </p>
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {searchQuery && totalResults === 0 && (
          <div className="text-center py-12">
            <div className="text-5xl mb-4">🔍</div>
            <p className="text-lg text-slate-600 mb-4">No results found</p>
            <Button onClick={() => setSearchQuery('')} variant="outline">Clear Search</Button>
          </div>
        )}

        {/* Footer CTA */}
        <div className="mt-12">
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="py-6">
              <h3 className="text-xl font-bold text-slate-800 mb-3 text-center">Ready to Get Started?</h3>
              <p className="text-slate-600 mb-4 text-center text-sm">Transform your authorship journey with AI-powered tools</p>
              <div className="flex gap-3 justify-center flex-wrap">
                <Button onClick={() => navigate('/user_dashboard')} className="bg-blue-600">Dashboard →</Button>
                <Button onClick={() => navigate('/prices')} variant="outline">View Pricing</Button>
                <Button onClick={() => navigate('/contact')} variant="outline">Contact Us</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {showBackToTop && (
        <button onClick={scrollToTop} className="fixed bottom-6 right-6 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full shadow-lg z-50" aria-label="Back to top">
          <span className="text-xl">↑</span>
        </button>
      )}
    </div>
  );
};

export default HelpCenterPage;
